import type { ContainerLine } from "@/types/api";
import type { MockBooking } from "@/types/booking";

export type ContainerChangeKind =
  | "qty"
  | "size"
  | "type"
  | "weight"
  | "dg"
  | "added"
  | "removed";

export interface ContainerChange {
  kind: ContainerChangeKind;
  label: string;
}

export interface ContainerDiff {
  effective: ContainerLine | null;
  source: ContainerLine | null;
  changes: ContainerChange[];
}

export function formatWeightKg(weight: number | null | undefined): string {
  if (weight == null) return "—";
  return `${Math.round(weight).toLocaleString()} kg`;
}

export function formatTonnage(kg: number): string {
  if (kg >= 1000) {
    const tonnes = kg / 1000;
    const formatted =
      tonnes % 1 === 0
        ? tonnes.toLocaleString()
        : tonnes.toLocaleString(undefined, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          });
    return `${formatted} t`;
  }
  return `${Math.round(kg).toLocaleString()} kg`;
}

export function formatLoadType(loadType: string | null | undefined): string | undefined {
  const value = loadType?.trim();
  if (!value) return undefined;
  return value.toUpperCase();
}

export function formatEquipmentSize(size: string | null | undefined): string {
  if (!size) return "—";
  return size.endsWith("'") ? size : `${size}'`;
}

export function cargoWeightKg(booking: Pick<MockBooking, "containers" | "weight">): number {
  const fromLines = (booking.containers ?? []).reduce(
    (sum, line) => sum + (line.weight_kg ?? 0),
    0,
  );
  if (fromLines > 0) return fromLines;
  return booking.weight ?? 0;
}

export function formatWeightSummary(
  booking: Pick<MockBooking, "containers" | "weight">,
): string | undefined {
  const totalKg = cargoWeightKg(booking);
  if (totalKg <= 0) return undefined;
  const totalUnits = (booking.containers ?? []).reduce(
    (sum, line) => sum + line.quantity,
    0,
  );
  if (totalUnits > 1) {
    return `${formatTonnage(totalKg)} (${formatTonnage(totalKg / totalUnits)}/unit)`;
  }
  return formatTonnage(totalKg);
}

function containerKeyFull(line: ContainerLine): string {
  return `${line.equipment_size ?? "?"}:${(line.equipment_type ?? "").toUpperCase()}`;
}

function containerKeySize(line: ContainerLine): string {
  return line.equipment_size ?? "?";
}

function containerKeyType(line: ContainerLine): string {
  return (line.equipment_type ?? "").toUpperCase();
}

function detectChanges(eff: ContainerLine, matched: ContainerLine): ContainerChange[] {
  const changes: ContainerChange[] = [];
  if (eff.quantity !== matched.quantity) {
    changes.push({
      kind: "qty",
      label: `Qty: ${matched.quantity} → ${eff.quantity}`,
    });
  }
  const effSize = eff.equipment_size ?? "";
  const srcSize = matched.equipment_size ?? "";
  if (effSize !== srcSize) {
    changes.push({
      kind: "size",
      label: `Size: ${formatEquipmentSize(srcSize || null)} → ${formatEquipmentSize(effSize || null)}`,
    });
  }
  const effType = (eff.equipment_type ?? "").toUpperCase();
  const srcType = (matched.equipment_type ?? "").toUpperCase();
  if (effType !== srcType) {
    changes.push({
      kind: "type",
      label: `Type: ${matched.equipment_type || "—"} → ${eff.equipment_type || "—"}`,
    });
  }
  const effWeight = eff.weight_kg ?? 0;
  const srcWeight = matched.weight_kg ?? 0;
  if (effWeight !== srcWeight) {
    if (srcWeight === 0) {
      changes.push({
        kind: "weight",
        label: `Weight added: ${effWeight.toLocaleString()} kg`,
      });
    } else if (effWeight === 0) {
      changes.push({
        kind: "weight",
        label: `Weight removed (was ${srcWeight.toLocaleString()} kg)`,
      });
    } else {
      changes.push({
        kind: "weight",
        label: `Weight: ${srcWeight.toLocaleString()} → ${effWeight.toLocaleString()} kg`,
      });
    }
  }
  if (Boolean(eff.is_dg) !== Boolean(matched.is_dg)) {
    changes.push({
      kind: "dg",
      label: eff.is_dg ? "DG flag added" : "DG flag removed",
    });
  }
  return changes;
}

/**
 * Compare effective containers against a prior snapshot using multi-pass
 * matching: exact key → size-only → type-only → unmatched.
 */
export function diffContainers(
  effective: ContainerLine[],
  source: ContainerLine[],
): ContainerDiff[] {
  const matchedEffIndices = new Set<number>();
  const matchedSrcIndices = new Set<number>();
  const pairs: [number, number][] = [];

  const matchPass = (keyFn: (line: ContainerLine) => string) => {
    const srcByKey = new Map<string, number[]>();
    for (let si = 0; si < source.length; si++) {
      if (matchedSrcIndices.has(si)) continue;
      const key = keyFn(source[si]);
      const arr = srcByKey.get(key) ?? [];
      arr.push(si);
      srcByKey.set(key, arr);
    }
    for (let ei = 0; ei < effective.length; ei++) {
      if (matchedEffIndices.has(ei)) continue;
      const candidates = srcByKey.get(keyFn(effective[ei]));
      if (candidates && candidates.length > 0) {
        const si = candidates.shift()!;
        if (candidates.length === 0) srcByKey.delete(keyFn(effective[ei]));
        pairs.push([ei, si]);
        matchedEffIndices.add(ei);
        matchedSrcIndices.add(si);
      }
    }
  };

  matchPass(containerKeyFull);
  matchPass(containerKeySize);
  matchPass(containerKeyType);

  const result: ContainerDiff[] = [];

  for (const [ei, si] of pairs) {
    const eff = effective[ei];
    const src = source[si];
    result.push({ effective: eff, source: src, changes: detectChanges(eff, src) });
  }

  for (let ei = 0; ei < effective.length; ei++) {
    if (!matchedEffIndices.has(ei)) {
      result.push({
        effective: effective[ei],
        source: null,
        changes: [{ kind: "added", label: "New line (not in booking source)" }],
      });
    }
  }

  for (let si = 0; si < source.length; si++) {
    if (!matchedSrcIndices.has(si)) {
      result.push({
        effective: null,
        source: source[si],
        changes: [{ kind: "removed", label: "Removed in update" }],
      });
    }
  }

  return result;
}

function previousUpdateContainers(booking: MockBooking): ContainerLine[] | null {
  const updates = booking.updates ?? [];
  if (updates.length < 2) return null;
  const prev = updates
    .filter((update) => !update.is_current)
    .sort((a, b) => b.version - a.version);
  return prev[0]?.containers?.length ? prev[0].containers : null;
}

export function buildContainerDiffs(booking: MockBooking): {
  diffs: ContainerDiff[];
  differs: boolean;
  isVersionDiff: boolean;
} {
  const containers = booking.containers ?? [];
  const sourceContainers = booking.source_containers ?? [];
  const previous = previousUpdateContainers(booking);
  const compareBase = previous ?? sourceContainers;
  const isVersionDiff = previous != null;
  const hasBaseline = Boolean(booking.update_source) && compareBase.length > 0;

  if (!hasBaseline) {
    return {
      diffs: containers.map((line) => ({
        effective: line,
        source: null,
        changes: [] as ContainerChange[],
      })),
      differs: false,
      isVersionDiff,
    };
  }

  const diffs = diffContainers(containers, compareBase);
  const differs = diffs.some((diff) => diff.changes.length > 0);
  if (!differs) {
    return {
      diffs: containers.map((line) => ({
        effective: line,
        source: null,
        changes: [],
      })),
      differs: false,
      isVersionDiff,
    };
  }

  return { diffs, differs: true, isVersionDiff };
}

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function summarizeContainerChanges(
  diffs: ContainerDiff[],
  isVersionDiff: boolean,
): string | null {
  const countKind = (kind: ContainerChangeKind) =>
    diffs.filter((diff) => diff.changes.some((change) => change.kind === kind)).length;

  const parts = [
    countKind("size") ? pluralize(countKind("size"), "size change") : null,
    countKind("type") ? pluralize(countKind("type"), "type change") : null,
    countKind("weight") ? pluralize(countKind("weight"), "weight change") : null,
    countKind("qty") ? pluralize(countKind("qty"), "qty change") : null,
    countKind("dg") ? pluralize(countKind("dg"), "DG change") : null,
    countKind("added") ? pluralize(countKind("added"), "added") : null,
    countKind("removed") ? pluralize(countKind("removed"), "removed") : null,
  ].filter(Boolean);

  if (parts.length === 0) return null;
  const prefix = isVersionDiff ? "Changes from previous update" : "Changes from source";
  return `${prefix}: ${parts.join(" · ")}`;
}
